import { useState, forwardRef, useCallback } from 'react';
import styled from 'styled-components';

import { Label, InputStyles, HelpText } from 'components/Form/Input';

import COLORS from 'styles/colors';
import { weight } from 'styles/typography';

import ChevronSvg from 'public/images/icons/chevron.svg';

const Container = styled.div`
  position: relative;
  outline: none;
`;

const Input = styled.input`
  ${InputStyles}
  padding: 0 15px;
  color: var(--color);
  cursor: pointer;
  appearance: none;
`;

const Chevron = styled(ChevronSvg)`
  position: absolute;
  top: 45px;
  right: 15px;
  width: 14px;
  height: auto;
  transform: ${({ $isActive }) =>
    $isActive ? 'rotate(180deg)' : 'rotate(0deg)'};
  transform-origin: 7px 8px;
  transition: 0.4s;
  pointer-events: none;

  path {
    stroke-width: 4px;
  }
`;

const Options = styled.div`
  position: absolute;
  top: 85px;
  width: 100%;
  border-radius: 10px;
  border: 1px solid ${COLORS.forest};
  background-color: ${COLORS.pinkLight};
  color: ${COLORS.forestBlack};
  font-weight: ${weight.medium};
  z-index: 20;
`;

const OptionItem = styled.div`
  padding: 12px 15px;
  border-bottom: 1px solid ${COLORS.blueGrey.opacity(0.3)};
  cursor: pointer;
  transition: 0.4s;

  &:hover {
    background-color: ${COLORS.forest.opacity(0.03)};
  }

  &:last-child {
    border: 0;
  }
`;

const SelectInput = forwardRef(
  ({ label, name, placeholder, options, setValue, errorMessage }, ref) => {
    const [optionValue, setOptionValue] = useState(null);
    const [showOptions, setShowOptions] = useState(false);

    const onKeyDownShowOptions = (e) => {
      if (e.key === 'Enter') {
        setShowOptions(!showOptions);
      }
    };

    const onSelect = useCallback(
      (option) => {
        setOptionValue(option);
        setShowOptions(false);
        setValue(name, option, { shouldValidate: true });
      },
      [name, setValue]
    );

    const onKeyDownSelect = (e, option) => {
      if (e.key === 'Enter') {
        onSelect(option);
      }
    };

    const styles = {
      '--color': optionValue
        ? COLORS.forestBlack
        : COLORS.forestBlack.opacity(0.5),
      '--border-color': showOptions
        ? COLORS.forest
        : COLORS.forest.opacity(0.5),
    };

    return (
      <Container
        onClick={() => setShowOptions(!showOptions)}
        onKeyDown={(e) => onKeyDownShowOptions(e)}
        role="button"
        tabIndex={0}
      >
        <Label htmlFor={name}>{label}</Label>
        <Input
          id={name}
          name={name}
          type="text"
          ref={ref}
          value={optionValue || ''}
          placeholder={optionValue || placeholder}
          style={styles}
          $hasError={errorMessage}
          readOnly
        />
        {showOptions && (
          <Options>
            {options?.map((option) => (
              <OptionItem
                key={option}
                role="button"
                tabIndex={0}
                onClick={() => onSelect(option)}
                onKeyDown={(e) => onKeyDownSelect(e, option)}
              >
                {option}
              </OptionItem>
            ))}
          </Options>
        )}
        <Chevron $isActive={showOptions} />
        <HelpText $hasError={errorMessage}>{errorMessage}</HelpText>
      </Container>
    );
  }
);

export default SelectInput;
